/**
 * One sport, and what it holds.
 *
 * `notFound()` is gone: in Next it threw a signal the framework turned into a
 * 404 page. There is no framework here to catch it, and a static bundle has no
 * status code to set anyway — so the "no such sport" case is rendered directly,
 * which is what the reader saw regardless.
 */

import { Link, useParams } from "react-router-dom";

import { IconCalendar, IconDatabase, IconPlus } from "@/components/Icons";
import {
  Button,
  Card,
  Empty,
  PageHead,
  Problem,
  Section,
  Stat,
  StatGrid,
} from "@/components/kit";
import {
  getDefinition,
  listDefinitions,
  listFixtures,
  listMasters,
  listRecords,
} from "@/lib/api/client";
import type { MasterSummary } from "@/lib/api/types";
import { displayName } from "@/lib/format";
import { sportEmoji } from "@/lib/sportIcons";
import { useApi } from "@/spa/useApi";

import styles from "./SportOverviewPage.module.css";

type MasterCount = { master: MasterSummary; count: number | null };

/** Row counts per master. A master whose binding isn't writable yet reports
 * null rather than a misleading zero. */
async function countRecords(sport: string, masters: MasterSummary[]): Promise<MasterCount[]> {
  return Promise.all(
    masters.map(async (master) => {
      if (!master.writable) return { master, count: null };
      // One row, for its total. This used to pull every record in the master to
      // find out how many there were, which is a page of work per tile.
      const page = await listRecords(sport, master.code, { limit: 1 });
      return { master, count: page.total };
    }),
  );
}

export function SportOverviewPage() {
  const { sport = "" } = useParams<{ sport: string }>();

  const { data, loading, error } = useApi(
    async () => {
      const all = await listDefinitions();
      const versions = all.filter((d) => d.sportCode === sport);
      // The segment doubles as the sport code; anything without a definition is
      // not a sport (this route catches every unmatched top-level path).
      if (versions.length === 0) return { missing: true as const };

      const latest = versions.reduce((a, b) => (b.version > a.version ? b : a));
      const published = versions.some((v) => v.status === "PUBLISHED");
      // A grouping — Aquatics — holds disciplines and hosts nothing itself. It
      // has no kind of fixture, so "enter a fixture" led to a screen with
      // nothing to choose. What it has instead is the sports inside it.
      const grouping = versions.some((v) => v.groups);
      const disciplines = grouping
        ? [...new Set(all.filter((d) => d.partOf === sport).map((d) => d.sportCode))].sort()
        : [];

      // Each discipline's own mark. It lives in that sport's document rather
      // than in the definitions list, so it is fetched per discipline — a
      // grouping holds a handful, and one that will not load shows the fallback.
      const iconByCode = new Map<string, string | null>(
        await Promise.all(
          disciplines.map(
            async (code) =>
              [code, (await getDefinition(code).catch(() => null))?.icon ?? null] as const,
          ),
        ),
      );

      let counts: MasterCount[] = [];
      let fixtureCount = 0;
      if (published) {
        const [masters, fixtures] = await Promise.all([
          listMasters(sport),
          listFixtures({ sport, limit: 1 }),
        ]);
        counts = await countRecords(sport, masters);
        fixtureCount = fixtures.total;
      }

      return { missing: false as const, latest, published, grouping, disciplines, iconByCode, counts, fixtureCount };
    },
    [sport],
    "failed to load",
  );

  if (error) return <Problem>Could not reach the admin service: {error}</Problem>;
  if (!data) return <PageHead title={displayName(sport)} note={loading ? "Loading…" : ""} />;
  if (data.missing) return <Problem>No sport called {sport}.</Problem>;

  const { latest, published, grouping, disciplines, iconByCode, counts, fixtureCount } = data;

  return (
    <>
      <PageHead
        title={displayName(sport)}
        note={`Version ${latest.version}`}
        actions={
          // No published/draft pill. It said nothing when published, and when
          // not published the panel below already says so at full size — the
          // same fact twice, once as a word and once as a paragraph.
          <div className={styles.actions}>
            <Link to={`/${sport}/setup`} className={styles.action}>
              Setup
            </Link>
            {published && !grouping && (
              <Link to={`/${sport}/fixtures/new`} className={styles.actionPrimary}>
                <IconPlus size={15} />
                Enter a fixture
              </Link>
            )}
          </div>
        }
      />

      {!published ? (
        <Card>
          <Empty
            title="Not published yet"
            note="This sport has a draft definition. Publishing it is what makes its data and its fixtures enterable."
            action={
              <Button tone="primary">
                <Link to={`/${sport}/setup`} style={{ color: "inherit", textDecoration: "none" }}>
                  Finish setting it up
                </Link>
              </Button>
            }
          />
        </Card>
      ) : counts.length === 0 ? (
        <Card>
          <Empty
            title="Nothing kept yet"
            note="This sport declares no things to keep, so there is nothing to load. Add them in setup."
            action={
              <Button tone="primary">
                <Link to={`/${sport}/setup`} style={{ color: "inherit", textDecoration: "none" }}>
                  Open setup
                </Link>
              </Button>
            }
          />
        </Card>
      ) : (
        <Section title="What it holds">
          <StatGrid>
            {counts.map(({ master, count }) => (
              <Stat
                key={master.code}
                label={master.label}
                value={count ?? "—"}
                note={count === null ? "not yet writable" : master.bindingKind}
                icon={<IconDatabase size={15} />}
                href={master.writable ? `/${sport}/masters/${master.code}` : undefined}
              />
            ))}
            {/* A grouping never holds a fixture, so a count of nought would be
                stating a rule as though it were a shortfall. */}
            {!grouping && (
              <Stat
                label="Fixtures"
                value={fixtureCount}
                note="entered so far"
                icon={<IconCalendar size={15} />}
                href={`/${sport}/fixtures`}
              />
            )}
          </StatGrid>
        </Section>
      )}

      {grouping && (
        <Section title="What sits inside it">
          {disciplines.length === 0 ? (
            <Card>
              <Empty
                title="No disciplines yet"
                note="A grouping is a place its disciplines sit inside. Add one and set its parent to this sport in setup."
              />
            </Card>
          ) : (
            <div className={styles.disciplines}>
              {disciplines.map((code) => (
                <Link key={code} to={`/${code}`} className={styles.discipline}>
                  <span className={styles.disciplineGlyph} aria-hidden>
                    {sportEmoji(iconByCode.get(code)).glyph}
                  </span>
                  <span className={styles.disciplineText}>
                    <span className={styles.disciplineName}>{displayName(code)}</span>
                    <span className={styles.disciplineNote}>Holds its own fixtures</span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Section>
      )}
    </>
  );
}
